import { ThrowError } from "@/api/callApi";
import Card from "@/components/common/Card";
import FormField from "@/components/common/FormField";
import FullPageLoader from "@/components/common/FullPageLoader";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { CreateOdPayload } from "@/interface/od.interface";
import { OdService } from "@/services/Od/OdService";
import { showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { errorTypes } from "@/utility/validation";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

type OdFormData = CreateOdPayload & {
  purpose: string;
  endDate?: string;
};

const CreateOd: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isFatchLoading, setIsFatchLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { siteId } = useCurrentUser();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm<OdFormData>({
    mode: "onChange",
    defaultValues: {
      duration: "FULL_DAY",
      startDate: "",
      endDate: "",
      numberOfDays: 1,
      location: "",
      expectedReturnTime: "",
      purpose: "",
    },
  });

  const watchedDuration = watch("duration");
  const watchedStartDate = watch("startDate");

  const watchedEndDate = watch("endDate");

  useEffect(() => {
    // Prevent overwriting initial loaded values during edit mode unless a field is modified
    if (isEdit && !dirtyFields.duration && !dirtyFields.startDate && !dirtyFields.endDate) {
      return;
    }


    // Calculate difference between start date and end date
    if (watchedDuration === "FIRST_HALF" || watchedDuration === "SECOND_HALF") {
      setValue("numberOfDays", 0.5, { shouldValidate: true, shouldDirty: true });
      clearErrors("numberOfDays");
      if (watchedStartDate) {
        setValue("endDate", watchedStartDate, { shouldValidate: true, shouldDirty: true });
      }
      return;
    }

    if (watchedStartDate && watchedEndDate) {
      const start = new Date(watchedStartDate);
      const end = new Date(watchedEndDate);
      const timeDiff = end.getTime() - start.getTime();
      const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
      if (dayDiff > 0) {
        setValue("numberOfDays", dayDiff, { shouldValidate: true, shouldDirty: true });
        clearErrors("numberOfDays");
        return;
      }
    }

    if (watchedDuration === "FULL_DAY") {
      setValue("numberOfDays", 1, { shouldValidate: true, shouldDirty: true });
      clearErrors("numberOfDays");
    }
  }, [watchedDuration, watchedStartDate, watchedEndDate, setValue, clearErrors, isEdit, dirtyFields.duration, dirtyFields.startDate, dirtyFields.endDate]);

  useEffect(() => {
    if (watchedStartDate && watchedEndDate) {
      if (new Date(watchedEndDate) < new Date(watchedStartDate)) {
        setValue("endDate", watchedStartDate, { shouldValidate: true, shouldDirty: true });
      } else {
        trigger("endDate");
      }
    }
  }, [watchedStartDate, watchedEndDate, setValue, trigger]);

  // Fetch data if editing
  useEffect(() => {
    if (!isEdit) return;

    const fetchOdData = async () => {
      setIsFatchLoading(true);
      try {
        const response = await OdService.getById(id!);
        if (response.status && response.data) {
          const data = response.data;
          reset({
            startDate: data.startDate,
            endDate: data?.endDate,
            location: data.location,
            purpose: data.reason,
            expectedReturnTime: data.expectedReturnTime || "",
            duration: data.duration || "FULL_DAY",
            numberOfDays: data.numberOfDays || 1,
          });
        } else {
          showToastNotification("danger", "Failed to load OD details");
          navigate(-1);
        }
      } catch (error) {
        showToastNotification("danger", "Error fetching OD");
        navigate(-1);
      } finally {
        setIsFatchLoading(false)
      }
    };
    fetchOdData();
  }, [id, isEdit, reset, navigate]);

  const onSubmit = async (data: OdFormData) => {
    const isHalfDay = data.duration === "FIRST_HALF" || data.duration === "SECOND_HALF";
    const payload: CreateOdPayload = {
      startDate: data.startDate,
      endDate: isHalfDay ? data.startDate : (data.endDate || data.startDate),
      reason: data.purpose,
      location: data.location,
      expectedReturnTime: data.expectedReturnTime,
      duration: data.duration,
      numberOfDays: isHalfDay ? 0.5 : Number(data.numberOfDays || 1),
      siteId: siteId,
    };



    setIsLoading(true);
    try {
      let response;
      if (isEdit) {
        response = await OdService.update(id!, payload);
      } else {
        response = await OdService.create(payload);
      }

      if (response?.status) {
        showToastNotification(
          "success",
          response?.message ||
          (isEdit ? "OD updated successfully" : "OD applied successfully"),
        );
        navigate(RoutesLink.myOd);
      }
    } catch (error) {
      ThrowError(error)
      // showToastNotification("danger", error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  if (isFatchLoading) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title={isEdit ? "Edit OD" : "Apply for OD"}
          isBackButtonVisible
          onClick={() => navigate(-1)}
        />
        <Card className="overflow-visible">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                type="select"
                label="Duration"
                value={watch("duration") ?? ""}
                options={[
                  { value: "", label: "Select Duration" },
                  { value: "FULL_DAY", label: "Full Day" },
                  { value: "FIRST_HALF", label: "First Half" },
                  { value: "SECOND_HALF", label: "Second Half" },
                ]}
                {...register("duration", {
                  ...errorTypes.required("Duration"),
                })}
                error={errors.duration?.message as string}
                required
              />

              <FormField
                {...register("startDate", {
                  ...errorTypes.required("Start Date"),
                  validate: (value: string) => {
                    if (!value) return true;
                    const minDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
                    if (value < minDate) {
                      return `Start Date cannot be earlier than last month (${moment(minDate).format("DD-MM-YYYY")})`;
                    }
                    const currentYearEnd = `${new Date().getFullYear()}-12-31`;
                    if (value > currentYearEnd) {
                      return `Start Date cannot exceed ${new Date().getFullYear()} (current year)`;
                    }
                    return true;
                  },
                })}
                type="date"
                label="Start Date"
                min={moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
                max={`${new Date().getFullYear()}-12-31`}
                error={errors.startDate?.message as string}
                required
              />

              <FormField
                {...register("endDate", {
                  ...errorTypes.required("End Date"),
                  validate: (value: string) => {
                    if (!value) return true;
                    if (watchedStartDate && value < watchedStartDate) {
                      return "End Date cannot be earlier than Start Date";
                    }
                    const currentYearEnd = `${new Date().getFullYear()}-12-31`;
                    if (value > currentYearEnd) {
                      return `End Date cannot exceed ${new Date().getFullYear()} (current year)`;
                    }
                    return true;
                  },
                })}
                type="date"
                label="End Date"
                min={watchedStartDate || moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
                max={`${new Date().getFullYear()}-12-31`}
                disabled={watchedDuration === "FIRST_HALF" || watchedDuration === "SECOND_HALF"}
                error={errors.endDate?.message as string}
                required
              />


              <FormField
                {...register("location", {
                  ...errorTypes.required("Location"),
                  ...errorTypes.name,
                })}
                type="text"
                label="Location"
                minLength={2}
                maxLength={30}
                placeholder="Enter location"
                error={errors.location?.message as string}
                required
              />


              <FormField
                {...register("expectedReturnTime", {
                  ...errorTypes.required("Expected Return Time"),
                })}
                type="time"
                label="Expected Return Time"
                placeholder="e.g., 18:30"
                error={errors.expectedReturnTime?.message as string}
                required
              />


              <div className="col-span-1">
                <FormField
                  {...register("numberOfDays", {
                    ...errorTypes.required("Number of Days"),
                    min: { value: 0.5, message: "Minimum 0.5 days" },
                  })}
                  type="number"
                  label="Number of Days"
                  placeholder="e.g., 0.5"
                  step="0.5"
                  error={errors.numberOfDays?.message as string}
                  readOnly
                  required
                />
              </div>

              <FormField
                {...register("purpose", {
                  ...errorTypes.required("Purpose"),
                  ...errorTypes.description,
                })}
                type="textarea"
                label="Purpose"
                rows={3}
                minLength={10}
                maxLength={500}
                placeholder="Enter purpose for OD"
                error={errors.purpose?.message as string}
                required
              />
            </div>

            {/* <div className="col-span-1">
              <FormField
                type="select"
                label="Duration"
                options={[
                  { value: "FULL_DAY", label: "Full Day" },
                  { value: "FIRST_HALF", label: "First Half" },
                  { value: "SECOND_HALF", label: "Second Half" },
                ]}
                error={errors.duration?.message as string}
              />
            </div> */}

            {/* <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700">
                Duration
              </label>
              <select
                {...register("duration", {
                  ...errorTypes.required("Duration"),
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Select an option</option>
                <option value="FULL_DAY">Full Day</option>
                <option value="FIRST_HALF">First Half</option>
                <option value="SECOND_HALF">Second Half</option>
              </select>
              {errors.duration && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.duration.message}
                </p>
              )}
            </div> */}



            {/* </div> */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading
                  ? isEdit
                    ? "Updating..."
                    : "Submitting..."
                  : isEdit
                    ? "Update OD"
                    : "Apply OD"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateOd;